import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaSuitcase,
  FaPlus,
  FaTrash,
  FaEdit,
  FaTimes,
  FaCheck,
  FaSync,
  FaMagic,
  FaGripVertical,
  FaChevronDown,
  FaChevronRight,
  FaCloudSun,
  FaUser,
  FaFolder,
  FaFolderOpen,
  FaArrowRight
} from 'react-icons/fa';
import {
  getPackingList,
  generatePackingList,
  createPackingCategory,
  updatePackingCategory,
  deletePackingCategory,
  createPackingItem,
  updatePackingItem,
  togglePackingItemCheck,
  deletePackingItem,
  reorderPackingItems
} from '../api/client';
import ConfirmDialog from './ConfirmDialog';
import './PackingList.css';

// Icon mapping for categories
const CATEGORY_ICONS = {
  'FaPassport': '📄',
  'FaTshirt': '👕',
  'FaSoap': '🧴',
  'FaMobileAlt': '📱',
  'FaFirstAid': '🩹',
  'FaHiking': '🥾',
  'FaBox': '📦'
};

const DEFAULT_COLORS = ['#4A7590', '#6BA37C', '#9B7BB8', '#D97706', '#DC2626', '#0891B2', '#7C3AED'];

const PackingList = ({ requestId, isOwner }) => {
  const [packingData, setPackingData] = useState({
    categories: [],
    items: [],
    uncategorized_items: [],
    stats: { total_items: 0, checked_items: 0, unchecked_items: 0, progress_percent: 0 }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // UI state
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [addingItemToCategoryId, setAddingItemToCategoryId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  // Form state
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState(DEFAULT_COLORS[0]);
  const [newItemName, setNewItemName] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState(1);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: null, id: null, name: '' });

  // Move item state
  const [movingItemId, setMovingItemId] = useState(null);

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Close move dropdown when clicking outside
  useEffect(() => {
    if (!movingItemId) return;
    const handleClick = (e) => {
      if (!e.target.closest('.move-dropdown') && !e.target.closest('.item-drag-handle')) {
        setMovingItemId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [movingItemId]);

  const fetchPackingList = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPackingList(requestId);
      // Only update state if component is still mounted
      if (!isMountedRef.current) return;
      setPackingData(data);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('Error fetching packing list:', err);
      setError('Failed to load packing list');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [requestId]);

  useEffect(() => {
    fetchPackingList();
  }, [fetchPackingList]);

  // Polling for multi-user sync (every 30 seconds)
  useEffect(() => {
    const pollInterval = setInterval(() => {
      getPackingList(requestId)
        .then(data => setPackingData(data))
        .catch(err => console.error('Polling error:', err));
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [requestId]);

  const handleGenerate = async (regenerateUncheckedOnly = false) => {
    setIsGenerating(true);
    setError(null);

    // Store current item count to detect when generation completes
    const initialItemCount = packingData.stats.total_items;

    try {
      // Start generation (returns immediately)
      await generatePackingList(requestId, {
        regenerateUncheckedOnly,
        preserveCustomItems: true,
        preserveCustomCategories: true
      });

      // Poll for results until new items appear (max 30 seconds)
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = 1000; // 1 second

      const pollForResults = async () => {
        try {
          const data = await getPackingList(requestId);

          // Check if generation is complete (new items appeared or categories exist)
          if (data.stats.total_items > initialItemCount || data.categories.length > 0) {
            setPackingData(data);
            setIsGenerating(false);
            return;
          }

          // Continue polling
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollForResults, pollInterval);
          } else {
            // Timeout - fetch whatever is available
            setPackingData(data);
            setIsGenerating(false);
          }
        } catch (err) {
          console.error('Error polling packing list:', err);
          setIsGenerating(false);
        }
      };

      // Start polling after a short delay
      setTimeout(pollForResults, 500);

    } catch (err) {
      console.error('Error generating packing list:', err);
      setError('Failed to generate packing list');
      setIsGenerating(false);
    }
  };

  const handleToggleCheck = async (itemId) => {
    try {
      await togglePackingItemCheck(requestId, itemId);
      // Optimistically update UI
      setPackingData(prev => {
        const updateItem = (items) => items.map(item =>
          item.id === itemId ? { ...item, is_checked: !item.is_checked } : item
        );
        return {
          ...prev,
          items: updateItem(prev.items),
          uncategorized_items: updateItem(prev.uncategorized_items),
          stats: {
            ...prev.stats,
            checked_items: prev.stats.checked_items + (prev.items.concat(prev.uncategorized_items).find(i => i.id === itemId)?.is_checked ? -1 : 1),
            unchecked_items: prev.stats.unchecked_items + (prev.items.concat(prev.uncategorized_items).find(i => i.id === itemId)?.is_checked ? 1 : -1),
            progress_percent: ((prev.stats.checked_items + (prev.items.concat(prev.uncategorized_items).find(i => i.id === itemId)?.is_checked ? -1 : 1)) / prev.stats.total_items) * 100
          }
        };
      });
    } catch (err) {
      console.error('Error toggling item check:', err);
      fetchPackingList();
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      await createPackingCategory(requestId, {
        name: newCategoryName.trim(),
        color: newCategoryColor
      });
      setNewCategoryName('');
      setNewCategoryColor(DEFAULT_COLORS[0]);
      setShowAddCategoryForm(false);
      await fetchPackingList();
    } catch (err) {
      console.error('Error creating category:', err);
      setError('Failed to create category');
    }
  };

  const handleAddItem = async (categoryId) => {
    if (!newItemName.trim()) return;

    try {
      await createPackingItem(requestId, {
        name: newItemName.trim(),
        quantity: newItemQuantity,
        category_id: categoryId || null
      });
      setNewItemName('');
      setNewItemQuantity(1);
      setAddingItemToCategoryId(null);
      await fetchPackingList();
    } catch (err) {
      console.error('Error creating item:', err);
      setError('Failed to create item');
    }
  };

  const handleDeleteItem = async () => {
    if (!confirmDialog.id) return;

    try {
      await deletePackingItem(requestId, confirmDialog.id);
      setConfirmDialog({ isOpen: false, type: null, id: null, name: '' });
      await fetchPackingList();
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Failed to delete item');
    }
  };

  const handleDeleteCategory = async () => {
    if (!confirmDialog.id) return;

    try {
      await deletePackingCategory(requestId, confirmDialog.id);
      setConfirmDialog({ isOpen: false, type: null, id: null, name: '' });
      await fetchPackingList();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category');
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      await updatePackingItem(requestId, itemId, updates);
      setEditingItem(null);
      await fetchPackingList();
    } catch (err) {
      console.error('Error updating item:', err);
      setError('Failed to update item');
    }
  };

  // Move item to a different category
  const handleMoveItem = async (itemId, newCategoryId) => {
    setMovingItemId(null);

    // Skip if same category
    const allItems = [...packingData.items, ...packingData.uncategorized_items];
    const item = allItems.find(i => i.id === itemId);
    if (item && item.category_id === newCategoryId) return;

    // Auto-expand target category
    if (newCategoryId) {
      setExpandedCategories(prev => ({ ...prev, [newCategoryId]: true }));
    }

    try {
      await reorderPackingItems(requestId, {
        items: [{
          item_id: itemId,
          new_category_id: newCategoryId === null ? 0 : newCategoryId,
          new_sort_order: 0
        }]
      });
      await fetchPackingList();
    } catch (err) {
      console.error('Error moving item:', err);
      setError('Failed to move item');
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getItemsForCategory = (categoryId) => {
    return packingData.items.filter(item => item.category_id === categoryId);
  };

  const renderProgressBar = () => {
    const { stats } = packingData;
    return (
      <div className="packing-progress">
        <div className="progress-info">
          <span className="progress-text">
            {stats.checked_items} of {stats.total_items} items packed
          </span>
          <span className="progress-percent">{stats.progress_percent.toFixed(0)}%</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${stats.progress_percent}%` }}
          />
        </div>
      </div>
    );
  };

  const renderItem = (item) => {
    const isEditing = editingItem?.id === item.id;

    if (isEditing) {
      return (
        <div key={item.id} className="packing-item editing">
          <input
            type="text"
            value={editingItem.name}
            onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
            className="edit-item-input"
            autoFocus
          />
          <input
            type="number"
            value={editingItem.quantity}
            onChange={(e) => setEditingItem({ ...editingItem, quantity: parseInt(e.target.value) || 1 })}
            className="edit-quantity-input"
            min="1"
            max="999"
          />
          <div className="edit-actions">
            <button
              onClick={() => handleUpdateItem(item.id, { name: editingItem.name, quantity: editingItem.quantity })}
              className="save-btn"
            >
              <FaCheck />
            </button>
            <button
              onClick={() => setEditingItem(null)}
              className="cancel-btn"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      );
    }

    const isMoving = movingItemId === item.id;

    return (
      <div
        key={item.id}
        className={`packing-item ${item.is_checked ? 'checked' : ''}`}
      >
        <div style={{ position: 'relative' }}>
          <div
            className="item-drag-handle"
            onClick={() => setMovingItemId(isMoving ? null : item.id)}
            title="Move item"
          >
            <FaGripVertical />
          </div>
          {isMoving && (
            <div className="move-dropdown">
              {packingData.categories.filter(cat => cat.id !== item.category_id).map(cat => (
                <button
                  key={cat.id}
                  className="move-dropdown-item"
                  onClick={() => handleMoveItem(item.id, cat.id)}
                >
                  <span style={{ color: cat.color || '#877B98' }}>●</span> {cat.name}
                </button>
              ))}
              {item.category_id && (
                <button
                  className="move-dropdown-item"
                  onClick={() => handleMoveItem(item.id, null)}
                >
                  <span style={{ color: '#8A827A' }}>●</span> Uncategorized
                </button>
              )}
            </div>
          )}
        </div>
        <label className="item-checkbox">
          <input
            type="checkbox"
            checked={item.is_checked}
            onChange={() => handleToggleCheck(item.id)}
          />
          <span className="checkmark"></span>
        </label>
        <div className="item-content">
          <span className={`item-name ${item.is_checked ? 'checked' : ''}`}>
            {item.name}
          </span>
          {item.quantity > 1 && (
            <span className="item-quantity">x{item.quantity}</span>
          )}
          {item.ai_reason && (
            <span className="item-reason" title={item.ai_reason}>
              <FaCloudSun />
            </span>
          )}
        </div>
        {!item.is_ai_generated && (
          <span className="item-badge custom">
            <FaUser title={`Added by ${item.user_name}`} />
          </span>
        )}
        <div className="item-actions">
          <button
            onClick={() => setEditingItem({ id: item.id, name: item.name, quantity: item.quantity })}
            className="action-btn edit"
            title="Edit"
          >
            <FaEdit />
          </button>
          <button
            onClick={() => setConfirmDialog({ isOpen: true, type: 'item', id: item.id, name: item.name })}
            className="action-btn delete"
            title="Delete"
          >
            <FaTrash />
          </button>
        </div>
      </div>
    );
  };

  const renderCategory = (category) => {
    const isExpanded = expandedCategories[category.id];
    const items = getItemsForCategory(category.id);
    const checkedCount = items.filter(i => i.is_checked).length;
    return (
      <div
        key={category.id}
        className="packing-category"
        style={{ borderLeftColor: category.color || '#877B98' }}
      >
        <div className="category-header" onClick={() => toggleCategory(category.id)}>
          <div className="category-left">
            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
            <span className="category-icon">
              {isExpanded ? <FaFolderOpen style={{ color: category.color }} /> : <FaFolder style={{ color: category.color }} />}
            </span>
            <span className="category-name">{category.name}</span>
            <span className="category-count">
              {checkedCount}/{items.length}
            </span>
          </div>
          <div className="category-actions" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setAddingItemToCategoryId(category.id)}
              className="category-action-btn add"
              title="Add item"
            >
              <FaPlus />
            </button>
            <button
              onClick={() => setConfirmDialog({ isOpen: true, type: 'category', id: category.id, name: category.name })}
              className="category-action-btn delete"
              title="Delete category"
            >
              <FaTrash />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="category-items">
            {items.map(item => renderItem(item))}

            {addingItemToCategoryId === category.id && (
              <div className="add-item-form">
                <input
                  type="text"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="Item name"
                  className="add-item-input"
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && handleAddItem(category.id)}
                />
                <input
                  type="number"
                  value={newItemQuantity}
                  onChange={(e) => setNewItemQuantity(parseInt(e.target.value) || 1)}
                  className="add-quantity-input"
                  min="1"
                  max="999"
                />
                <button onClick={() => handleAddItem(category.id)} className="add-item-btn">
                  <FaCheck />
                </button>
                <button onClick={() => { setAddingItemToCategoryId(null); setNewItemName(''); }} className="cancel-item-btn">
                  <FaTimes />
                </button>
              </div>
            )}

            {items.length === 0 && addingItemToCategoryId !== category.id && (
              <div className="empty-category">
                No items. <button onClick={() => setAddingItemToCategoryId(category.id)}>Add one</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderUncategorizedItems = () => {
    if (packingData.uncategorized_items.length === 0 && addingItemToCategoryId !== 'uncategorized') {
      return null;
    }

    return (
      <div
        className="packing-category uncategorized"
      >
        <div className="category-header">
          <div className="category-left">
            <FaFolder style={{ color: '#8A827A' }} />
            <span className="category-name">Uncategorized</span>
            <span className="category-count">
              {packingData.uncategorized_items.filter(i => i.is_checked).length}/{packingData.uncategorized_items.length}
            </span>
          </div>
          <div className="category-actions">
            <button
              onClick={() => setAddingItemToCategoryId('uncategorized')}
              className="category-action-btn add"
              title="Add item"
            >
              <FaPlus />
            </button>
          </div>
        </div>
        <div className="category-items">
          {packingData.uncategorized_items.map(item => renderItem(item))}

          {addingItemToCategoryId === 'uncategorized' && (
            <div className="add-item-form">
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Item name"
                className="add-item-input"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleAddItem(null)}
              />
              <input
                type="number"
                value={newItemQuantity}
                onChange={(e) => setNewItemQuantity(parseInt(e.target.value) || 1)}
                className="add-quantity-input"
                min="1"
                max="999"
              />
              <button onClick={() => handleAddItem(null)} className="add-item-btn">
                <FaCheck />
              </button>
              <button onClick={() => { setAddingItemToCategoryId(null); setNewItemName(''); }} className="cancel-item-btn">
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="packing-container">
      {/* Header */}
      <div className="packing-header">
        <div className="packing-header-left">
          <h2><FaSuitcase /> Packing List</h2>
          {packingData.stats.total_items > 0 && (
            <span className="packing-count">{packingData.stats.total_items} items</span>
          )}
        </div>
        <div className="packing-header-right">
          {packingData.stats.total_items > 0 && packingData.stats.unchecked_items > 0 && (
            <button
              onClick={() => handleGenerate(true)}
              className="packing-resuggest-btn"
              disabled={isGenerating}
              title="Re-suggest unchecked items"
            >
              {isGenerating ? <FaSync className="spinning" /> : <FaMagic />}
              <span className="hidden sm:inline">Re-suggest</span>
            </button>
          )}
          <button
            onClick={fetchPackingList}
            className="packing-refresh-btn"
            disabled={isLoading}
            title="Refresh"
          >
            <FaSync className={isLoading ? 'spinning' : ''} />
          </button>
          {packingData.stats.total_items === 0 && (
            <button
              onClick={() => handleGenerate(false)}
              className="packing-generate-btn"
              disabled={isGenerating}
            >
              {isGenerating ? <FaSync className="spinning" /> : <FaMagic />}
              Generate Packing List
            </button>
          )}
          <button
            onClick={() => setShowAddCategoryForm(true)}
            className="packing-add-category-btn"
            title="Add category"
          >
            <FaPlus /> Category
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="packing-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}><FaTimes /></button>
        </div>
      )}

      {/* Add Category Form */}
      {showAddCategoryForm && (
        <div className="add-category-form">
          <div className="add-category-form-header">
            <h3>Add Category</h3>
            <button onClick={() => setShowAddCategoryForm(false)} className="form-close-btn">
              <FaTimes />
            </button>
          </div>
          <div className="add-category-form-body">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Category name"
              className="category-name-input"
              autoFocus
              onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
            />
            <div className="color-picker">
              {DEFAULT_COLORS.map(color => (
                <button
                  key={color}
                  className={`color-option ${newCategoryColor === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setNewCategoryColor(color)}
                />
              ))}
            </div>
            <div className="form-actions">
              <button onClick={() => setShowAddCategoryForm(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleAddCategory} className="submit-btn" disabled={!newCategoryName.trim()}>
                <FaCheck /> Add Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {packingData.stats.total_items > 0 && renderProgressBar()}

      {/* Content */}
      <div className="packing-list">
        {isLoading && packingData.categories.length === 0 ? (
          <div className="packing-loading">
            <FaSync className="spinning" />
            <p>Loading packing list...</p>
          </div>
        ) : packingData.stats.total_items === 0 && !isGenerating ? (
          <div className="packing-empty">
            <FaSuitcase />
            <h3>No packing list yet</h3>
            <p>Generate an AI-powered packing list based on your trip details and weather forecast.</p>
            <button onClick={() => handleGenerate(false)} className="generate-first-btn">
              <FaMagic /> Generate Packing List
            </button>
          </div>
        ) : isGenerating && packingData.stats.total_items === 0 ? (
          <div className="packing-loading">
            <FaSync className="spinning" />
            <p>Generating personalized packing suggestions...</p>
          </div>
        ) : (
          <>
            {packingData.categories.map(category => renderCategory(category))}
            {renderUncategorizedItems()}
          </>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.type === 'category' ? 'Delete Category' : 'Delete Item'}
        message={confirmDialog.type === 'category'
          ? `Are you sure you want to delete the category "${confirmDialog.name}"? Items in this category will become uncategorized.`
          : `Are you sure you want to delete "${confirmDialog.name}"?`
        }
        onConfirm={confirmDialog.type === 'category' ? handleDeleteCategory : handleDeleteItem}
        onCancel={() => setConfirmDialog({ isOpen: false, type: null, id: null, name: '' })}
        confirmText="Delete"
        confirmType="danger"
      />
    </div>
  );
};

export default PackingList;
